import { useAuthStore } from '@/store/auth';

/**
 * Tenant Security Utilities
 * These functions ensure secure tenant ID extraction from authenticated user context
 */

/**
 * Get current user tenant ID from authentication store
 * This is the secure way to get tenant ID from authenticated user
 */
export const getCurrentTenantId = (): string | null => {
  try {
    const { user, isHydrated } = useAuthStore.getState();

    // Ensure auth store is hydrated before accessing user data
    if (!isHydrated) {
      console.warn(
        'Auth store not yet hydrated, tenant ID may not be available'
      );
      return null;
    }

    // Return tenant ID from authenticated user
    if (user?.tenantId) {
      return user.tenantId;
    }

    return null;
  } catch (error) {
    console.error('Error getting tenant ID from auth store:', error);
    return null;
  }
};

/**
 * Get current user tenant ID with fallback for development
 * SECURITY: Only use this in development/testing environments
 */
export const getCurrentTenantIdWithFallback = (): string => {
  // Try to get tenant ID from authenticated user first
  const tenantId = getCurrentTenantId();

  if (tenantId) {
    return tenantId;
  }

  // DEVELOPMENT FALLBACK ONLY
  if (process.env.NODE_ENV === 'development') {
    const fallbackTenantId =
      process.env.NEXT_PUBLIC_DEFAULT_TENANT_ID || 'default-tenant';
    console.warn(
      `🚨 Using fallback tenant ID for development: ${fallbackTenantId}`
    );
    return fallbackTenantId;
  }

  // Production: No tenant ID available
  throw new Error(
    'SECURITY ERROR: Cannot determine user tenant ID - user not authenticated'
  );
};

/**
 * Validate that user is authenticated and has a valid tenant
 */
export const validateUserTenant = (): {
  isValid: boolean;
  tenantId: string | null;
  error?: string;
} => {
  try {
    const { user, token, isHydrated } = useAuthStore.getState();

    // Check if auth store is hydrated
    if (!isHydrated) {
      return {
        isValid: false,
        tenantId: null,
        error: 'Authentication not yet loaded',
      };
    }

    // Check if user is authenticated
    if (!token || !user) {
      return {
        isValid: false,
        tenantId: null,
        error: 'User not authenticated',
      };
    }

    // Check if tenant ID exists
    if (!user.tenantId) {
      return {
        isValid: false,
        tenantId: null,
        error: 'User has no tenant ID',
      };
    }

    // Validate tenant ID format (customize based on your tenant ID format)
    if (
      user.tenantId.length < 3 ||
      user.tenantId === 'undefined' ||
      user.tenantId === 'null'
    ) {
      return {
        isValid: false,
        tenantId: user.tenantId,
        error: 'Invalid tenant ID format',
      };
    }

    return {
      isValid: true,
      tenantId: user.tenantId,
    };
  } catch (error) {
    return {
      isValid: false,
      tenantId: null,
      error: `Tenant validation error: ${error}`,
    };
  }
};

/**
 * React hook to get current tenant ID
 */
export const useCurrentTenant = () => {
  const user = useAuthStore((state) => state.user);
  const isHydrated = useAuthStore((state) => state.isHydrated);

  return {
    tenantId: user?.tenantId || null,
    isLoading: !isHydrated,
    isAuthenticated: !!user,
    hasValidTenant: !!(user?.tenantId && user.tenantId.length > 0),
  };
};

/**
 * Check if current user belongs to a specific tenant
 */
export const isUserInTenant = (targetTenantId: string): boolean => {
  const currentTenantId = getCurrentTenantId();
  return currentTenantId === targetTenantId;
};

/**
 * Get user info with tenant context for logging/debugging
 */
export const getUserTenantInfo = () => {
  const { user, isHydrated } = useAuthStore.getState();

  return {
    userId: user?.id || null,
    tenantId: user?.tenantId || null,
    email: user?.email || null,
    isHydrated,
    timestamp: new Date().toISOString(),
  };
};
